import { useEffect, useRef } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  missedCallsApi,
  type CallbackDismissOutcome,
  type CallbackItem,
  type CallbackList,
  type CallbackScope,
} from '../services/missedCalls'
import { telephonyMissedCalls } from '../services/telephonyMissedCalls'
import { useCallPhase } from '../telephony/selectors'

/**
 * #193 — LA LISTE « À RAPPELER », source unique du panneau du téléphone ET du
 * badge de l'en-tête (même cache : les deux affichent toujours le même compte).
 *
 * La vérité est SERVEUR : un numéro sort de la liste parce que la base dit
 * qu'il a été rappelé (ou classé), jamais parce qu'un écran l'a retiré de sa
 * mémoire. Trois déclencheurs de rechargement, tous par INVALIDATION :
 *   ① `call.missed` (socket /telephony, room tenant) — un nouveau manqué ;
 *   ② chaque changement de PHASE du poste (composition, fin d'appel,
 *      qualification) — le rappel que je viens de lancer retire sa ligne ;
 *   ③ un filet de 60 s : le rappel passé par un COLLÈGUE n'émet rien vers mon
 *      poste, et un manqué survenu socket coupée est perdu (limite S0-D).
 *
 * Zone gelée `telephony/` : on en LIT un module (`selectors`), on n'y écrit
 * rien — c'est pourquoi la clé n'est pas ajoutée à
 * `useCallEndRefresh` et que la phase est observée ici.
 */

export const CALLBACK_LIST_QUERY_KEY = ['missed-calls', 'list'] as const

export type { CallbackDismissOutcome }

/** Filet de relecture (exporté pour son spec). */
export const CALLBACK_LIST_REFRESH_MS = 60_000

export interface UseCallbackListResult {
  data: CallbackList | undefined
  isLoading: boolean
  isError: boolean
  /** Classe le numéro (« ne pas rappeler ») — auteur tracé côté serveur. */
  dismiss: (item: CallbackItem, outcome?: CallbackDismissOutcome) => Promise<void>
  /** Numéro en cours de classement (désactive sa ligne). */
  dismissingNumber: string | null
  refresh: () => void
}

export function useCallbackList(
  options: { scope?: CallbackScope; enabled?: boolean } = {},
): UseCallbackListResult {
  // Filtre de l'ADMIN. Pour un agent le serveur l'ignore : sa portée vient du
  // rôle de son jeton.
  const scope: CallbackScope = options.scope ?? 'all'
  const enabled = options.enabled !== false
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: [...CALLBACK_LIST_QUERY_KEY, { scope }],
    queryFn: () => missedCallsApi.list(scope),
    enabled,
    staleTime: 15_000,
    refetchInterval: enabled ? CALLBACK_LIST_REFRESH_MS : false, // ③
    // Y2 : au changement de filtre, on garde la liste précédente à l'écran
    // le temps de la réponse — sinon le panneau disparaît (380 px de saut).
    placeholderData: keepPreviousData,
  })

  // ① Nouveau manqué constaté par le serveur.
  useEffect(() => {
    if (!enabled) return
    const onMissed = () => {
      void queryClient.invalidateQueries({ queryKey: CALLBACK_LIST_QUERY_KEY })
    }
    telephonyMissedCalls.onMissedCall(onMissed)
    return () => telephonyMissedCalls.offMissedCall(onMissed)
  }, [enabled, queryClient])

  // ② Le poste a changé de phase : un appel vient d'être lancé, de finir ou
  // d'être qualifié. `useSyncExternalStore` rend plusieurs fois par
  // transition — la phase précédente évite de rejouer l'invalidation.
  const phase = useCallPhase()
  const previousPhase = useRef(phase)
  useEffect(() => {
    if (previousPhase.current === phase) return
    previousPhase.current = phase
    if (!enabled) return
    void queryClient.invalidateQueries({ queryKey: CALLBACK_LIST_QUERY_KEY })
  }, [phase, enabled, queryClient])

  const mutation = useMutation({
    mutationFn: (variables: { item: CallbackItem; outcome?: CallbackDismissOutcome }) =>
      missedCallsApi.dismiss(variables.item.number, variables.outcome),
    onSettled: () => queryClient.invalidateQueries({ queryKey: CALLBACK_LIST_QUERY_KEY }),
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    dismiss: async (item, outcome) => {
      await mutation.mutateAsync({ item, outcome })
    },
    dismissingNumber: mutation.isPending ? (mutation.variables?.item.number ?? null) : null,
    refresh: () => {
      void queryClient.invalidateQueries({ queryKey: CALLBACK_LIST_QUERY_KEY })
    },
  }
}
